import {
    AppBorderBox,
    AppButton,
    AppModal,
    AppPaginator,
    AppPaper,
    AppSkeletonGlobalCardList,
    AppText,
    AppTitle,
} from "../../../commons/components";
import AppBox from "../../../commons/components/Box";
import {useAppDispatch, useAppSelector} from "../../../hooks/app";
import {StatusEnum} from "../../../commons/enums/status-enum";
import {Can} from "../../../casl/Can";
import {RolePolicyActionEnum} from "../../../commons/enums/role-policy-action.enum";
import IconAdd from "../../../commons/components/icons/add";
import {useEffect, useState} from "react";
import IconBranch from "../../../commons/components/icons/branch";
import IconBin from "../../../commons/components/icons/bin";
import {ManageFootballCompetition} from "../../../commons/models/football/admin/manage-football-competition";
import {useSnackbar} from "../../../redux/snackbarProvider";
import {
    fetchManageHostedCompetitionList
} from "../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-list-slice";
import {cleanUpStepperFormData} from "../../../redux/slices/stepper-form-slice";
import {
    hostedCompetitionDelete
} from "../../../redux/slices/football/admin/team/hosted-competition/manage-team-hosted-competition-delete-slice";
import {AxiosError} from "axios";
import {IServerErrorResponse} from "../../../commons/components/interface";
import HostedTournamentCard from "./components/hostedTournament/HostedTournamentCard";
import usePagination from "../../../hooks/usePagination";
import FilterToolbar from "../../../commons/components/ui-components/FilterToolbar";
import useFilter from "../../../hooks/useFilter";
import TournamentAddEdit from "../tournaments/components/tournamentAddEdit/TournamentAddEdit";
import TournamentDelete from "../tournaments/components/TournamentDelete";
import TournamentDeleteInprogress from "../tournaments/components/TournamentDeleteInprogress";

export default function TeamManageHostedTournament() {
    const dispatch = useAppDispatch();
    const addSnackbar = useSnackbar();
    const {teamInfo} = useAppSelector((state) => state.footballManageTeam);
    const {hostedCompetitionList, hostedCompetitionListStatus} = useAppSelector(
        (state) => state.footballManageHostedCompetitionList
    );
    const {competitionDeleteStatus} = useAppSelector((state) => state.footballManageCompetitionDelete);
    const [isDeleteHostedTournament, setIsDeleteHostedTournament] = useState(false);
    const [isAddEditHostedTournament, setIsAddEditHostedTournament] = useState(false);
    const [selectedHostedTournament, setSelectedHostedTournament] = useState<ManageFootballCompetition | null>(null);
    const {page, perPage, pageLimitChange, pageChange} = usePagination();
    const {searchText, sortOrder, sortBy} = useFilter();
    const { hostedCompetitionAddResponseStatus } = useAppSelector((state) => state.footballManageHostedCompetitionAdd);
    const { hostedCompetitionEditResponseStatus } = useAppSelector((state) => state.footballManageHostedCompetitionEdit);

    useEffect(() => {
        if (teamInfo.id) {
            dispatch(
                fetchManageHostedCompetitionList({
                    teamId: teamInfo.id,
                    urlParams: {
                        sort: sortBy,
                        sortOrder,
                        page,
                        perPage,
                        name: searchText
                    },
                })
            );
        }
    }, [teamInfo, page, perPage, sortBy, sortOrder, searchText]);

    const reFetchHostedTournament = () => {
        dispatch(
            fetchManageHostedCompetitionList({
                teamId: teamInfo.id,
                urlParams: {
                    sort: sortBy,
                    sortOrder,
                    name: searchText
                },
            })
        );
    }

    const handleAfterAddEdit = () => {
        setSelectedHostedTournament(null);
        setIsAddEditHostedTournament(false);
        reFetchHostedTournament();
    };
    const handleAfterAddEditClose = () => {
        setSelectedHostedTournament(null);
        setIsAddEditHostedTournament(false);
        dispatch(cleanUpStepperFormData());
    };

    const handleDeleteConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedHostedTournament) {
            setIsDeleteHostedTournament(false);
            dispatch(
                hostedCompetitionDelete({
                    teamId: teamInfo.id,
                    hostedCompetitionId: selectedHostedTournament.id,
                })
            )
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "hosted-tournament-delete-success",
                        text: "Hosted Tournament Deleted Successfully",
                        variant: "success",
                    });
                    reFetchHostedTournament();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
            setSelectedHostedTournament(null);
        }
    };

    return (
        <>
            {hostedCompetitionListStatus === StatusEnum.Loading ? (
                <AppSkeletonGlobalCardList withAvatar withText numberOfItems={5}/>
            ) : (
                <AppBox mb="sm" gap="sm" flexDirection="column">
                    <AppBox alignItems="center" justifyContent="space-between">
                        <AppTitle as="h5">Tournaments</AppTitle>
                        <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                            {hostedCompetitionList.length > 0 && (
                                <AppButton
                                    label="Add Tournament"
                                    variant="light"
                                    onClick={() => setIsAddEditHostedTournament(true)}
                                />
                            )}
                        </Can>
                    </AppBox>
                    <FilterToolbar enableStatus={false} sortByOptions={[
                        {
                            title: "Title",
                            value: "title",
                        },
                        {
                            title: "Created Date",
                            value: "createdAt",
                        }
                    ]}/>
                    {hostedCompetitionList.length < 1 ? (
                        <AppPaper padding="sm" shadow="xs">
                            <AppBorderBox borderVariant="dashed">
                                <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center"
                                        p="sm">
                                    <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="xs">
                                        <AppBorderBox radius="rounded" p="2xs">
                                            <IconAdd width={40} height={40} color="rgba(var(--border-200))"/>
                                        </AppBorderBox>
                                        <AppText fontWeight="semibold">
                                            There are not any tournaments hosted in the team.{" "}
                                            <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                                                To add a tournament, click 'Add Tournament'.
                                            </Can>
                                        </AppText>
                                    </AppBox>
                                    <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                                        <AppButton label="Add Tournament"
                                                   onClick={() => setIsAddEditHostedTournament(true)}/>
                                    </Can>
                                </AppBox>
                            </AppBorderBox>
                        </AppPaper>
                    ) : (
                        <AppBox flexDirection="column" gap="md">
                            <AppBox flexDirection="column" gap="sm">
                                {hostedCompetitionList.mapArray((competition, index) => (
                                    <HostedTournamentCard
                                        teamInfo={teamInfo}
                                        hostedTournamentInfo={competition}
                                        key={index}
                                        onDelete={() => {
                                            setIsDeleteHostedTournament(true);
                                            setSelectedHostedTournament(competition);
                                        }}
                                        onEdit={() => {
                                            setIsAddEditHostedTournament(true);
                                            setSelectedHostedTournament(competition);
                                        }}
                                    />
                                ))}
                            </AppBox>
                            {hostedCompetitionList?.pageCount > 1 &&
                                <AppPaginator
                                    perPage={hostedCompetitionList.perPage}
                                    pageCount={hostedCompetitionList.pageCount}
                                    currentPage={hostedCompetitionList.page}
                                    pageChange={pageChange}
                                    perPageLimitChange={pageLimitChange}/>
                            }
                        </AppBox>
                    )}
                </AppBox>
            )}
            <AppModal
                modalSize="lg"
                title={selectedHostedTournament ? "Edit Tournament" : "Add A Tournament"}
                titleIcon={<IconBranch/>}
                opened={isAddEditHostedTournament}
                onClose={handleAfterAddEditClose}
                withoutButtons
            withLoadingButton={
                hostedCompetitionAddResponseStatus === StatusEnum.Loading ||
                hostedCompetitionEditResponseStatus === StatusEnum.Loading
            }
            >
                <TournamentAddEdit
                    hostTeam={teamInfo}
                    competitionData={selectedHostedTournament}
                    onSave={handleAfterAddEdit}
                />
            </AppModal>
            {selectedHostedTournament && (
                <>
                    <AppModal
                        opened={isDeleteHostedTournament}
                        onClose={() => {
                            setSelectedHostedTournament(null);
                            setIsDeleteHostedTournament(false);
                        }}
                        title="Want To Delete This Tournament?"
                        titleIcon={<IconBin/>}
                        iconColor="danger"
                        confirmButtonLabel="Delete"
                        onSave={(e) => {
                            handleDeleteConfirm(e as Event);
                        }}
                    >
                        <TournamentDelete tournament={selectedHostedTournament}/>
                    </AppModal>
                    <AppModal
                        opened={competitionDeleteStatus === StatusEnum.Loading}
                        title="Deleting Tournament"
                        titleIcon={<IconBin/>}
                        iconColor="danger"
                        withoutCancelButton
                        withoutButtons
                        withoutCloseButton
                    >
                        <TournamentDeleteInprogress tournament={selectedHostedTournament}/>
                    </AppModal>
                </>
            )}
        </>
    );
}
